/**
 * Hook to check whether the given email is on the free-access whitelist
 * (emails granted free app access without an active subscription).
 * Whitelist is managed from the admin panel and stored in Firestore
 * (`freeAccessWhitelist/{normalizedEmail}`), read directly here so it stays
 * live if an admin adds/removes an email while the app is open.
 *
 * Being on the list is not enough by itself: each entry also carries an
 * `accessStart`/`accessEnd` window (set by the admin — 30 days, a year, or a
 * custom range; both null means unlimited), so access is only granted while
 * the current time falls inside that window.
 */
import { useEffect, useState } from 'react';
import { doc, getFirestore, onSnapshot, Timestamp } from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);

const LOG = '[FreeAccessWhitelist]';

function isWithinAccessWindow(data: {
  accessStart?: Timestamp | null;
  accessEnd?: Timestamp | null;
}): boolean {
  const now = Date.now();
  if (data.accessStart && data.accessStart.toMillis() > now) return false;
  if (data.accessEnd && data.accessEnd.toMillis() < now) return false;
  return true;
}

export function useIsFreeAccessWhitelisted(email: string | null): boolean {
  const [isWhitelisted, setIsWhitelisted] = useState(false);

  useEffect(() => {
    if (!email) {
      setIsWhitelisted(false);
      return;
    }
    const normalized = email.trim().toLowerCase();
    const ref = doc(db, 'freeAccessWhitelist', normalized);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => setIsWhitelisted(snap.exists() && isWithinAccessWindow(snap.data() ?? {})),
      (err) => {
        console.warn(LOG, 'listener error', err);
        setIsWhitelisted(false);
      },
    );
    return () => unsubscribe();
  }, [email]);

  return isWhitelisted;
}

/**
 * Same check as useIsFreeAccessWhitelisted, but across a set of emails —
 * used to check "is anyone in my linked family whitelisted" so a whitelisted
 * parent's (or child's) free access also unlocks premium for the account
 * linked to them, mirroring how an active subscription already does.
 * Returns true as soon as any one of the emails is currently whitelisted.
 */
export function useIsAnyEmailWhitelisted(emails: string[] | null): boolean {
  const [isWhitelisted, setIsWhitelisted] = useState(false);

  useEffect(() => {
    if (!emails || emails.length === 0) {
      setIsWhitelisted(false);
      return;
    }
    const normalized = Array.from(
      new Set(emails.map((e) => e.trim().toLowerCase()).filter(Boolean)),
    );
    if (normalized.length === 0) {
      setIsWhitelisted(false);
      return;
    }

    const whitelistedByEmail = new Map<string, boolean>();
    const recompute = (): void => {
      setIsWhitelisted(Array.from(whitelistedByEmail.values()).some(Boolean));
    };

    const unsubscribers = normalized.map((normalizedEmail) => {
      const ref = doc(db, 'freeAccessWhitelist', normalizedEmail);
      return onSnapshot(
        ref,
        (snap) => {
          whitelistedByEmail.set(
            normalizedEmail,
            snap.exists() && isWithinAccessWindow(snap.data() ?? {}),
          );
          recompute();
        },
        (err) => {
          console.warn(LOG, 'listener error', err);
          whitelistedByEmail.set(normalizedEmail, false);
          recompute();
        },
      );
    });

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [emails]);

  return isWhitelisted;
}
